import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SoftLaunchConfig } from '../../core/config/environments.config';
import { BYPASS_TOKEN_INJECTION } from '../../core/interceptors/fsx.interceptor.service';

/**
 * Optional runtime override read from `assets/runtime-config.json`. Any key it
 * specifies wins over the baked per-environment default; anything it omits keeps
 * that default. An empty file (or a failed fetch) leaves the env default intact.
 */
interface RuntimeConfigOverride {
  /** Partial Soft Launch override; only the keys present are applied. */
  softLaunch?: Partial<SoftLaunchConfig>;
}

/** Path to the runtime config asset, served from the deployed webroot. */
const RUNTIME_CONFIG_URL = '/assets/runtime-config.json';

/**
 * Resolves the effective Prod Soft Launch config (AB172621): the per-environment
 * default from `environment.softLaunch` (DEV/UAT off, PROD on), optionally
 * overridden at runtime by the deployed `assets/runtime-config.json` so the
 * full-launch cutover needs no code deployment (R4).
 *
 * {@link RuntimeConfigService.load} runs from an app initializer, so the config is
 * resolved before routing and guards can read the flags synchronously. A missing
 * or unreadable file leaves the environment default in place.
 */
@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
  /** Angular HttpClient used to fetch the config asset. */
  private readonly http = inject(HttpClient);

  /**
   * The effective Soft Launch config. Seeded from the per-environment default and
   * refined by any runtime override once {@link RuntimeConfigService.load} resolves.
   */
  private softLaunch: SoftLaunchConfig = { ...environment.softLaunch };

  /**
   * Fetches `assets/runtime-config.json` and applies any override it contains over
   * the environment default. Bypasses the auth interceptor (public file, loads
   * before sign-in) and cache-busts so a redeployed config is picked up. On any
   * error the environment default is kept.
   * @returns an Observable that completes once the config is resolved.
   */
  load(): Observable<void> {
    const url = `${RUNTIME_CONFIG_URL}?cb=${Date.now()}`;
    const context = new HttpContext().set(BYPASS_TOKEN_INJECTION, true);
    return this.http.get<RuntimeConfigOverride>(url, { context }).pipe(
      tap(override => this.applyOverride(override)),
      map(() => undefined),
      catchError((error: unknown) => {
        console.error(
          'Failed to load runtime-config.json; keeping the environment Soft Launch defaults.',
          error,
        );
        return of(undefined);
      }),
    );
  }

  /**
   * Whether the given email's domain is on the approved login-domain list.
   * Exact, case-insensitive match on the domain after the final `@`; a missing
   * or malformed email is not approved (fail closed).
   * @param email the user's email address from the identity token
   * @returns true when the email's domain is approved
   */
  isApprovedLoginDomain(email: string | null | undefined): boolean {
    if (!email || !email.includes('@')) {
      return false;
    }
    const domain = email.split('@').pop()!.trim().toLowerCase();
    return this.softLaunch.approvedLoginDomains.some(
      approved => approved.trim().toLowerCase() === domain,
    );
  }

  /**
   * Merges a runtime override over the current (environment-default) config. Each
   * key present in the override wins; omitted keys (and an empty domain list) keep
   * the environment default.
   * @param override the parsed runtime override, or null
   */
  private applyOverride(override: RuntimeConfigOverride | null): void {
    const softLaunch = override?.softLaunch;
    if (!softLaunch) {
      return;
    }
    this.softLaunch = {
      restrictSelfRegistration:
        softLaunch.restrictSelfRegistration ?? this.softLaunch.restrictSelfRegistration,
      restrictLoginDomains:
        softLaunch.restrictLoginDomains ?? this.softLaunch.restrictLoginDomains,
      approvedLoginDomains:
        softLaunch.approvedLoginDomains && softLaunch.approvedLoginDomains.length > 0
          ? softLaunch.approvedLoginDomains
          : this.softLaunch.approvedLoginDomains,
    };
  }

  /** Whether self-service registration is currently blocked (R1/AC2). */
  get restrictSelfRegistration(): boolean {
    return this.softLaunch.restrictSelfRegistration;
  }

  /** Whether login is currently restricted to the approved domains (R2/AC4). */
  get restrictLoginDomains(): boolean {
    return this.softLaunch.restrictLoginDomains;
  }
}
